import { isDefined } from '@sgme/fp';
import { useBatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { useField } from 'components/common-form/hooks/useField';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { NewAmountInput } from 'components/shared/NewAmountInput';
import { ReadOnlyAmount } from 'components/shared/NewAmountInput/ReadOnlyAmount';
import { FC, useMemo } from 'react';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

export const AmountField: FC<ProductNodeBuilderProps> = ({ node: { id }, workspaceType, tabIndex }) => {
  const { metadata, data, setData } = useField<'AmountField'>(id);
  const isFieldInvalid = useIsFieldInvalid(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const {
    isCellInactive,
    onEditingKeyDown: onKeyDown,
    isFocused,
    lostInputBeforeEditing,
    isSelectAll,
    decimalPadding,
  } = useBatchNavigationContext({ decimalPadding: metadata?.decimalPadding });
  const labelKey = `form.${metadata?.translationKey}`;
  const tooltipTranslationKey = metadata?.hasTooltip ? (`form.${metadata?.translationKey}.tooltip` as any) : undefined;
  const lostInputValue = useMemo<number | undefined>(() => {
    return lostInputBeforeEditing ? Number(lostInputBeforeEditing) : undefined;
  }, [lostInputBeforeEditing]);

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={labelKey as any}
      workspaceType={workspaceType}
      tooltipKey={tooltipTranslationKey}
      minWidth={metadata?.minWidth}
      maxWidth={metadata?.maxWidth}
      tooltipLocation={metadata?.tooltipLocation}
      className={patchedFieldClassName}
    >
      {metadata?.isReadOnly && (
        <ReadOnlyAmount
          value={data}
          precision={metadata?.nbDecimals}
          currency={metadata?.currency ?? ''}
          e2e={id}
          decimalPadding={decimalPadding}
          tabIndex={tabIndex}
          className={isCellInactive ? 'pe-none' : ''}
        />
      )}
      {!metadata?.isReadOnly && (
        <NewAmountInput
          placeholderKey={isDefined(metadata?.translationKey) ? (labelKey as any) : undefined}
          value={lostInputValue ?? data}
          onValueChanged={setData}
          precision={metadata?.nbDecimals}
          currency={metadata?.currency ?? ''}
          hasError={isFieldInvalid}
          e2e={id}
          decimalPadding={decimalPadding}
          hidePlaceholder={metadata?.hidePlaceholder}
          tabIndex={tabIndex}
          onKeyDown={onKeyDown}
          isFocused={isFocused}
          isSelectAll={isSelectAll}
          className={isCellInactive ? 'pe-none' : ''}
        />
      )}
    </BlockOrientationFormGroup>
  );
};

AmountField.displayName = 'AmountField';
